import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { findExistingWheelForCategory } from './findExistingWheel';
import { DemandWheel } from '@/components/sector/DemandWheel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Category {
  id: number;
  category_name: string;
  // iter-8 PR #3167 BLOCKING-2: stored canonical from site_categories.
  // Prefer this over breadcrumb-name reconstruction; reconstruction can
  // diverge from stored (case, whitespace, ampersand-encoding, translations).
  category_path?: string;
  level: number;
  product_count: number;
  parent_id: number | null;
  children?: Category[];
  children_count?: number;
}

interface CategoryWheelData {
  id: number;
  category_name: string;
  relative_demand_score: number;
  avg_monthly_searches: number;
  trend_yoy_pct?: number;
  children?: CategoryWheelData[];
}

export default function CategoryTree() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [categoryTree, setCategoryTree] = useState<Category[]>([]);
  const [currentView, setCurrentView] = useState<Category | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<Category[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedLeafCategory, setSelectedLeafCategory] = useState<string | null>(null);
  // iter-5: also capture the canonical FULL category_path (constructed from
  // breadcrumb at click time) so handleGenerateWheel can pass it through the
  // navigation state to QuestionWheelResearch.
  const [selectedLeafCategoryPath, setSelectedLeafCategoryPath] = useState<string | null>(null);
  const [checkingWheel, setCheckingWheel] = useState(false);

  useEffect(() => {
    if (selectedSite?.id || selectedSite?._id) {
      loadCategoryTree();
      loadStats();
    }
  }, [selectedSite]);

  const loadCategoryTree = async () => {
    try {
      setLoading(true);
      const siteId = selectedSite?.id || selectedSite?._id;
      const response = await authFetch(`${API_URL}/sites/${siteId}/categories/tree`);

      if (response.ok) {
        const data = await response.json();
        setCategoryTree(data.tree);
      } else {
        console.error('Failed to load category tree');
      }
    } catch (error) {
      console.error('Error loading category tree:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const siteId = selectedSite?.id || selectedSite?._id;
      const response = await authFetch(`${API_URL}/sites/${siteId}/categories/stats`);

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading category stats:', error);
    }
  };

  const convertToWheelData = (categories: Category[]): CategoryWheelData[] => {
    return categories.map(cat => ({
      id: cat.id,
      category_name: cat.category_name,
      relative_demand_score: cat.product_count || 0,
      avg_monthly_searches: cat.product_count || 0,
      children: cat.children ? convertToWheelData(cat.children) : []
    }));
  };

  const handleCategoryClick = async (category: CategoryWheelData) => {
    // Find the full category object with children
    const findCategory = (cats: Category[], id: number): Category | null => {
      for (const cat of cats) {
        if (cat.id === id) return cat;
        if (cat.children) {
          const found = findCategory(cat.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const fullCategory = currentView
      ? findCategory(currentView.children || [], category.id)
      : findCategory(categoryTree, category.id);

    if (fullCategory && fullCategory.children && fullCategory.children.length > 0) {
      // Has children - drill down in the tree
      setCurrentView(fullCategory);
      setBreadcrumb(prev => [...prev, fullCategory]);
    } else if (fullCategory) {
      // Leaf category - check for existing question wheel.
      // iter-8 (PR #3167 BLOCKING-2): PREFER stored fullCategory.category_path
      // (post-iter-8 API change includes it from site_categories.category_path).
      // Fallback to breadcrumb reconstruction for backwards-compat with any
      // older API response that doesn't include the field — but this is
      // documented as fragile (display names may diverge from stored canonical).
      const canonicalPath = fullCategory.category_path
        || [
            ...breadcrumb.map((b) => b.category_name),
            fullCategory.category_name,
          ].join(' > ');
      await handleLeafCategoryClick(fullCategory.category_name, canonicalPath);
    }
  };

  const handleLeafCategoryClick = async (categoryName: string, categoryPath?: string) => {
    if (!selectedSite?.id) return;

    setCheckingWheel(true);
    try {
      // Check if a question wheel already exists for this category.
      //
      // iter-6 (PR #3167 BLOCKING-2): use category_path-aware matching when
      // available (list endpoint now returns category_path per iter-6
      // backend change). Pre-iter-6 leaf-only matching could load the
      // WRONG wheel for the clicked canonical path when 2+ siblings share
      // the same leaf (e.g. 'Men > Shoes' vs 'Women > Shoes' both
      // center_term='shoes'). Match priority:
      //   1. Exact category_path === categoryPath (canonical disambiguator)
      //   2. Fallback: leaf-only center_term match, but only when there's
      //      EXACTLY ONE leaf candidate (ambiguous → don't silently pick;
      //      treat as "no wheel" so user is prompted to generate)
      const response = await authFetch(`/api/sites/${selectedSite.id}/question-wheels`);
      const wheels = await response.json();

      // PR #3167 iter-19 (codex CR-2 MINOR): wheel-matching logic
      // extracted to pure helper for focused unit testing. The match
      // priority + fallback semantics (canonical-path exact match →
      // unambiguous leaf-name fallback restricted to wheel_type='category')
      // are behaviorally important (wrong-wheel selection on same-leaf
      // siblings is a real category-disambiguation bug) and now have
      // explicit test coverage in findExistingWheel.test.ts.
      const existingWheel = findExistingWheelForCategory(wheels, categoryName, categoryPath);

      if (existingWheel) {
        // Wheel exists - navigate to it
        toast({
          title: 'Question Wheel Found',
          description: `Loading existing analysis for "${categoryName}"`,
        });
        navigate('/research/question-wheel', {
          state: {
            categoryName,
            // iter-5: pass canonical category_path (constructed from breadcrumb)
            categoryPath,
            wheelId: existingWheel.id,
            fromCategoryTree: true
          }
        });
      } else {
        // No wheel exists - prompt to generate
        setSelectedLeafCategory(categoryName);
        // iter-5: capture canonical path for handleGenerateWheel's navigation state
        setSelectedLeafCategoryPath(categoryPath || null);
        setShowGenerateDialog(true);
      }
    } catch (error) {
      console.error('Error checking for wheel:', error);
      toast({
        title: 'Error',
        description: 'Failed to check for existing question wheel',
        variant: 'destructive'
      });
    } finally {
      setCheckingWheel(false);
    }
  };

  const handleGenerateWheel = () => {
    if (!selectedLeafCategory) return;

    setShowGenerateDialog(false);

    // Navigate to Question Wheel page with category pre-filled
    navigate('/research/question-wheel', {
      state: {
        categoryName: selectedLeafCategory,
        // iter-5: pass canonical category_path so producer write-path lands
        // canonical (not free-form leaf)
        categoryPath: selectedLeafCategoryPath,
        fromCategoryTree: true,
        autoGenerate: true
      }
    });

    toast({
      title: 'Generating Question Wheel',
      description: `Creating analysis for "${selectedLeafCategory}"`,
    });
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Go back to root
      setCurrentView(null);
      setBreadcrumb([]);
    } else {
      // Go to specific breadcrumb level
      const newBreadcrumb = breadcrumb.slice(0, index + 1);
      setCurrentView(newBreadcrumb[newBreadcrumb.length - 1]);
      setBreadcrumb(newBreadcrumb);
    }
  };

  const currentCategories = currentView ? currentView.children || [] : categoryTree;
  const wheelData = convertToWheelData(currentCategories);
  const centerLabel = currentView ? currentView.category_name : selectedSite?.name || 'Categories';
  const centerScore = currentCategories.reduce((sum, cat) => sum + (cat.product_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle>No Site Selected</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Please select a site from the dropdown to view its category tree.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (categoryTree.length === 0) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle>No Categories Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p>No product categories have been imported for this site yet.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Run the category import script to populate the category tree.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Category Tree</h1>
          <p className="text-muted-foreground">
            {selectedSite.name} - Product Category Hierarchy
          </p>
        </div>
        <Button onClick={() => navigate('/sites')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Sites
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Categories</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total?.total_categories || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Level</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentView ? `Level ${currentView.level + 1}` : 'Top Level'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Categories Shown</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentCategories.length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className="text-primary hover:underline"
          >
            Root
          </button>
          {breadcrumb.map((item, index) => (
            <div key={item.id} className="flex items-center gap-2">
              <span className="text-muted-foreground">/</span>
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className={index === breadcrumb.length - 1 ? 'font-semibold' : 'text-primary hover:underline'}
              >
                {item.category_name}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Category Wheel */}
      <Card>
        <CardContent className="pt-6">
          {wheelData.length > 0 ? (
            <div>
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  💡 <strong>Tip:</strong> Click on leaf categories (categories without subcategories) to research questions people ask about that topic.
                </p>
              </div>
              <DemandWheel
                data={wheelData}
                onCategoryClick={handleCategoryClick}
                centerLabel={centerLabel}
                centerScore={centerScore}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                This category has no subcategories.
              </p>
              <Button
                onClick={() => handleBreadcrumbClick(breadcrumb.length - 2)}
                className="mt-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go Back
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Question Wheel Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Question Wheel</DialogTitle>
            <DialogDescription>
              No question wheel exists yet for "{selectedLeafCategory}". Would you like to generate one?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              A question wheel will analyze what questions people are asking about this category,
              including search volumes, competition metrics, and content opportunities.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateWheel}>
              Generate Question Wheel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
